import { useEffect, useRef } from 'react';

// eslint-disable-next-line no-process-env
const DEFAULT_DEBOUNCE_TIME = process.env.NODE_ENV === 'test' ? 0 : 300;

/**
 * Fire a callback when `value` changes and remains changed
 * for the debounce period, unless `pauseDebounce` returns true.
 */
export function useOnDebounce<T>(
  callback: (value: T) => void,
  value: T,
  delay = DEFAULT_DEBOUNCE_TIME,
  pauseDebounce: (() => boolean) | undefined = undefined,
) {
  const callbackRef = useRef(callback);
  const timerRef = useRef<number | null>(null);
  const lastValueRef = useRef(value);

  // Keep latest callback without retriggering debounce
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Ignore if paused or no meaningful change
    if (pauseDebounce?.()) {
      lastValueRef.current = value;
      return;
    }
    lastValueRef.current = value;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(() => {
      callbackRef.current(value);
    }, delay);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [value, delay, pauseDebounce]);
}

// **** Export **** //

export default useOnDebounce;
